import { useCallback } from "react";
import { cloneDeep } from "lodash";
import { dataModelToQueryString } from "@/entities/data-model";
import type { SearchDataModel } from "@/entities/data-model";
import { GENDER_FIELD_KEY, genderFormToDataModelValue } from "@/entities/data-model/genderValue";
import { useMetadataParamsContext, useMetadataQuery } from "@/entities/form-metadata";
import type { UserPreferences } from "@/entities/user-preferences";
import { useSubmitHandler } from "@/shared/submit-handler";
import { pendingNavigation } from "@/shared/utils/pendingNavigation";
import type { FormValues } from "../form";
import { DEFAULT_GIVEN_NAME_EXACTNESS, DEFAULT_SURNAME_EXACTNESS } from "../form/utils";
import { mapFormEventsToDataModel } from "../metadata-converters/mapFormEventsToDataModel";
import { mapTimespanToDataModel } from "../metadata-converters/mapTimespanToDataModel";

interface HandleSubmitProps {
    formValues: FormValues;
    userPreference: UserPreferences | null;
}

export const useFormControls = (dataModel: SearchDataModel, isEditForm: boolean) => {
    const metadataParams = useMetadataParamsContext();
    const customSubmitHandler = useSubmitHandler();
    const { data: formMetadata } = useMetadataQuery();

    const convertFormValuesToDataModel = useCallback(
        (formValues: FormValues) => {
            // Exclude passThroughData
            const { passThroughData, ...rest } = dataModel;
            const mappedFormEventsToDataModelEvents = mapFormEventsToDataModel(formValues);
            const timespan = mapTimespanToDataModel(formValues.timespan, formMetadata.features);

            const fields = { ...formValues.fields };
            if (fields[GENDER_FIELD_KEY]) {
                fields[GENDER_FIELD_KEY] = {
                    ...fields[GENDER_FIELD_KEY],
                    value: genderFormToDataModelValue(fields[GENDER_FIELD_KEY].value),
                };
            }

            return {
                ...rest,
                filters: {
                    ...dataModel.filters,
                    recordSubject: formValues.recordSubject,
                },
                name: formValues.name,
                family: formValues.family,
                events: mappedFormEventsToDataModelEvents,
                treePerson: formValues.treePerson,
                types: formValues.types,
                priority: formValues.priority,
                location: formValues.location,
                fields,
                timespan,
                showUnviewedRecordsOnly: formValues.showUnviewedRecordsOnly,
            } as SearchDataModel;
        },
        [dataModel, formMetadata],
    );

    const applyUserPreferences = useCallback(
        (newDataMode: SearchDataModel, userPreference: UserPreferences | null) => {
            const pageSize = isEditForm ? newDataMode.page.size : userPreference?.UserPreferences?.ResultsPageSize;
            return {
                ...newDataMode,
                page: {
                    ...newDataMode.page,
                    size: pageSize ?? 20,
                },
            };
        },
        [isEditForm],
    );

    const handleExactnessPresence = (formValues: FormValues) => {
        const formValuesCopy = cloneDeep(formValues);
        if (!formValuesCopy.name.givenName) {
            formValuesCopy.name.givenNameExactness = DEFAULT_GIVEN_NAME_EXACTNESS;
        }
        if (!formValuesCopy.name.surname) {
            formValuesCopy.name.surnameExactness = DEFAULT_SURNAME_EXACTNESS;
        }
        Object.values(formValuesCopy.family).forEach((relation) => {
            relation.instances.forEach((instance) => {
                if (!instance.givenName) {
                    instance.givenNameExactness = DEFAULT_GIVEN_NAME_EXACTNESS;
                }
                if (!instance.surname) {
                    instance.surnameExactness = DEFAULT_SURNAME_EXACTNESS;
                }
            });
        });
        Object.values(formValuesCopy.events).forEach((event) => {
            event.instances.forEach((instance) => {
                if (instance.dateProximity?.year != null) {
                    instance.dateProximity.month = 0;
                    instance.dateProximity.day = 0;
                }
                if (!instance.location) {
                    instance.locationExactness = null;
                }
            });
        });
        Object.values(formValuesCopy.fields).forEach((field) => {
            if (!field.value) {
                field.isExact = false;
            }
            // Ensure canonicalizer-required string properties are never null
            field.relation = field.relation ?? "";
            field.eventName = field.eventName ?? "";
            field.assertion = field.assertion ?? "";
        });
        return formValuesCopy;
    };

    const handleSubmit = useCallback(
        async ({ formValues, userPreference }: HandleSubmitProps) => {
            const processedFormValues = handleExactnessPresence(formValues);

            let newDataModel = convertFormValuesToDataModel(processedFormValues);
            newDataModel = applyUserPreferences(newDataModel, userPreference);
            newDataModel = { ...newDataModel, page: { ...newDataModel.page, number: 1 } };
            let query = dataModelToQueryString(newDataModel, "search");
            if (dataModel.passThroughData) {
                const delimiter = query.length ? "&" : "";
                query = `${query}${delimiter}${dataModel.passThroughData}`;
            }

            if (customSubmitHandler) {
                await customSubmitHandler({ dataModel: newDataModel, query });
            } else {
                let basePath = "/search";
                if ("contentType" in metadataParams && !!metadataParams.contentType) {
                    basePath =
                        metadataParams.contentType === "collection"
                            ? `/search/collections/${metadataParams.formId}`
                            : `/search/categories/${metadataParams.formId}`;
                }
                window.open(`${basePath}?${query}`, "_self");
                return pendingNavigation();
            }
        },
        [dataModel, metadataParams, applyUserPreferences, convertFormValuesToDataModel, customSubmitHandler],
    );

    return {
        handleSubmit,
    };
};
